import { Toaster } from "@/components/ui/toaster";
import { lazy, Suspense } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, MutationCache } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Outlet, Navigate, useLocation } from "react-router-dom";

import GardforsaljningPage from "./pages/GardforsaljningPage";
import HomePage from "./pages/HomePage";
import Explore from "./pages/Explore";
import About from "./pages/About";
import KontaktPage from "./pages/KontaktPage";
import VillkorPage from "./pages/VillkorPage";
import IntegritetPage from "./pages/IntegritetPage";
import FavoritesPage from "./pages/FavoritesPage";
import PlaceDetail from "./pages/PlaceDetail";
import ProducerDetail from "./pages/ProducerDetail";
import ProducerBioPage from "./pages/ProducerBioPage";
import ProducerShop from "./pages/ProducerShop";
import ProducerProductDetail from "./pages/ProducerProductDetail";
import ProducerProductOrderPage from "./pages/producer/ProducerProductOrderPage";
import FleaMarketDetailPage from "./pages/FleaMarketDetailPage";
import FleaMarketOccurrenceDetailPage from "./pages/FleaMarketOccurrenceDetailPage";
import ExperiencePage from "./pages/ExperiencePage";
import AreaPage from "./pages/AreaPage";
import DesignPreview from "./pages/DesignPreview";
import NotFound from "./pages/NotFound";
import Unauthorized from "./pages/Unauthorized";
import Error500 from "./pages/Error500";
import { AppErrorBoundary } from "./components/errors/AppErrorBoundary";
import Login from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";
import CustomerOverview from "./pages/account/CustomerOverview";
import MyBookingsPage from "./pages/account/MyBookingsPage";
import CustomerBookingDetailPage from "./pages/account/CustomerBookingDetailPage";
import MyOrdersPage from "./pages/account/MyOrdersPage";
import OrderDetailPage from "./pages/account/OrderDetailPage";
import CustomerProfilePage from "./pages/account/CustomerProfilePage";
import InvitationsPage from "./pages/account/InvitationsPage";
import ProducerPortal from "./pages/ProducerPortal";
import ProducerFarmDashboard from "./pages/ProducerFarmDashboard";
import SuggestPage from "./pages/SuggestPage";
import BookingCancelPage from "./pages/BookingCancelPage";
import AccommodationDetailPage from "./pages/AccommodationDetailPage";
import AccommodationBookingCancelPage from "./pages/AccommodationBookingCancelPage";
import {
  ProducerProductList,
  ProducerProductCreate,
  ProducerProductEdit,
  ProducerGrundinfo,
  ProducerPlats,
  ProducerKontakt,
  ProducerKategorier,
  ProducerBilder,
  ProducerPublicering,
  ProducerShopOverview,
  ProducerOrdersPage,
  ProducerEventsPage,
  ProducerFleaMarketsPage,
  EventEditorPage,
  EventBookingsPage,
  ProducerTeamPage,
  ProducerTeamInvitationsPage,
  ProducerTeamMemberPage,
  ProducerBillingPage,
  ProducerTermsPage,
  ProducerShippingPage,
  ProducerVerificationPage,
  ProducerMerPage,
  ProducerPlacePresentationPage,
  ProducerPickupWindowsPage,
  ProducerPackListPage,
  ProducerAccommodationsPage,
  AccommodationEditorPage,
  ProducerBookingsHubPage,
  AccommodationBookingDetailPage,
  ProducerOversattningar,
} from "./pages/producer";
import ProducerWelcomeFlowPage from "./pages/producer/ProducerWelcomeFlowPage";
import ProducerDashboardPage from "./pages/producer/ProducerDashboardPage";
import { ProducerFeatureGate } from "./components/producer/ProducerFeatureGate";
import ForProducersEntry from "./pages/for-producers/ForProducersEntry";
import ForProducersQualify from "./pages/for-producers/ForProducersQualify";
import ForProducersQualifySuccess from "./pages/for-producers/ForProducersQualifySuccess";
import ForProducersApply from "./pages/for-producers/ForProducersApply";
import AppProducerOnboarding from "./pages/producer/AppProducerOnboarding";
import OnboardingSuccessPage from "./pages/producer/OnboardingSuccessPage";
import ProducerSetupPage from "./pages/producer/ProducerSetupPage";
import { ProducerSetupLayout } from "./layouts/ProducerSetupLayout";
import { AuthedForProducersRedirect } from "./components/routing/AuthedForProducersRedirect";
import EventDetailPage from "./pages/EventDetailPage";
import CuratedWeekendPage from "./pages/CuratedWeekendPage";
import CheckoutSuccessPage from "./pages/CheckoutSuccessPage";
import ProducerActivitiesEmbedPage from "./pages/embed/ProducerActivitiesEmbedPage";
import ActivityEmbedPage from "./pages/embed/ActivityEmbedPage";
import ProductEmbedPage from "./pages/embed/ProductEmbedPage";
import ProducerTermsLegalPage from "./pages/legal/ProducerTermsLegalPage";
import AdminLegalDocumentsPage from "./pages/admin/AdminLegalDocumentsPage";
import {
  AdminProducers,
  AdminProducerNew,
  AdminProducerEdit,
  AdminProducerOverview,
  AdminProducerGrundinfo,
  AdminProducerPlats,
  AdminProducerKontakt,
  AdminProducerKategorier,
  AdminProducerBilder,
  AdminProducerPublicering,
  AdminProductList,
  AdminProductCreate,
  AdminProductEdit,
  AdminLogin,
  AdminCallback,
  AdminDashboardPage,
  AdminSystemLogsPage,
  AdminPlacesPage,
  AdminPlacesIngestPage,
  AdminPlaceSuggestionsPage,
  AdminPlaceEditPage,
  AdminProducerInterestPage,
  AdminPlaceReportsPage,
  AdminExperienceCurationPage,
  AdminEmailOutboxPage,
  AdminDraftCleanup,
  AdminHostDebugPage,
  AdminHostHealthPage,
  AdminProducerSuccessPage,
  AdminControlPanelPage,
  AdminAreasPage,
  AdminAreaEditPage,
} from "./pages/admin";
import { AdminProducerProvider } from "./context/AdminProducerContext";
import { AdminRouteGuard } from "./components/admin/AdminRouteGuard";
import { AuthRouteGuard } from "./components/AuthRouteGuard";
import { FarmCartProvider } from "./context/FarmCartContext";
import { AuthProvider } from "./context/AuthContext";
import { ActivePortalProvider } from "./context/ActivePortalContext";
import { UnifiedAppLayout } from "./components/layout/UnifiedAppLayout";
import AuthCallback from "./pages/AuthCallback";
import AcceptInvitePage from "./pages/AcceptInvitePage";
import { GlobalCartSheet, MobileCartBar } from "./components/farmshop";
import { LegacyPrefixRedirect } from "./components/routing/LegacyPrefixRedirect";
import { OutingRedirect } from "./components/routing/OutingRedirect";
import { ProducerPortalGate } from "./components/producer";
import { RouteAnalytics } from "./components/analytics/RouteAnalytics";
import { HostRouteGuard } from "./components/routing/HostRouteGuard";
import { FavoritesProvider } from "./components/favorites/FavoritesProvider";
import { InlineBannerProvider } from "./context/InlineBannerContext";
import { AuthSheetProvider } from "./components/auth/AuthSheetProvider";
import { LocaleProvider } from "@/i18n";

const EditorDashboardPage = lazy(() => import("./pages/editor/EditorDashboardPage"));
const EditorPlacesListPage = lazy(() => import("./pages/editor/EditorPlacesListPage"));
const EditorOnboardingPage = lazy(() => import("./pages/editor/EditorOnboardingPage"));
const EditorPlaceEditPage = lazy(() => import("./pages/editor/EditorPlaceEditPage"));
const EditorAreasListPage = lazy(() => import("./pages/editor/EditorAreasListPage"));
const EditorAreaEditPage = lazy(() => import("./pages/editor/EditorAreaEditPage"));
const SocialPublishingPage = lazy(() => import("./pages/editor/SocialPublishingPage"));
const AdminPlaceEditorsPage = lazy(() => import("./pages/admin/AdminPlaceEditorsPage"));
const AdminAmbassadorsPage = lazy(() => import("./pages/admin/AdminAmbassadorsPage"));
const AdminAmbassadorDetailPage = lazy(() => import("./pages/admin/AdminAmbassadorDetailPage"));
const AdminPlaceStudioPage = lazy(() => import("./pages/admin/AdminPlaceStudioPage"));
const EditorCuratedPagesPage = lazy(() => import("./pages/admin/AdminCuratedPagesPage"));

import { assertNotSupportPreview, SupportPreviewBlockedError } from "@/lib/supportPreview";
import { toast as sonnerToast } from "sonner";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
  mutationCache: new MutationCache({
    onMutate: () => {
      // Central guard: block ALL mutations when support preview is active
      assertNotSupportPreview();
    },
    onError: (error) => {
      // Show user-friendly toast for blocked mutations (don't show for other errors)
      if (error instanceof SupportPreviewBlockedError) {
        sonnerToast.error('Supportläge', {
          description: 'Ändringar är inte tillåtna i supportläge.',
        });
      }
    },
  }),
});

/**
 * Inner app component that has access to Router context
 * Needed because ActivePortalProvider uses useNavigate
 */
function AppRoutes() {
  const location = useLocation();
  const isEmbedRoute = location.pathname.startsWith('/embed/');

  return (
    <AuthSheetProvider>
    <ActivePortalProvider>
      <RouteAnalytics />
      <Routes>
        {/* ============================================ */}
        {/* LEGACY ROUTE REDIRECTS                      */}
        {/* Preserves full subpath, query string & hash */}
        {/* ============================================ */}
        <Route path="/account/*" element={<LegacyPrefixRedirect fromPrefix="/account" toPrefix="/app/customer" />} />
        <Route path="/producer/*" element={<LegacyPrefixRedirect fromPrefix="/producer" toPrefix="/app/producer" />} />
        <Route path="/admin/*" element={<LegacyPrefixRedirect fromPrefix="/admin" toPrefix="/app/admin" />} />

        {/* Public routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/gardforsaljning" element={<GardforsaljningPage />} />
        <Route path="/gardforsaljning/:regionSlug" element={<GardforsaljningPage />} />
        <Route path="/home" element={<Navigate to="/" replace />} />
        <Route path="/explore" element={<Explore />} />
        <Route path="/upplevelse/:slug" element={<ExperiencePage />} />
        <Route path="/omraden/:slug" element={<AreaPage />} />
        <Route path="/design-preview" element={<DesignPreview />} />
        <Route path="/place/:slug" element={<PlaceDetail />} />
        <Route path="/g/:slug" element={<ProducerBioPage />} />
        <Route path="/producer/:slugOrId" element={<ProducerDetail />} />
        <Route path="/producer/:slugOrId/shop" element={<ProducerShop />} />
        <Route path="/producer/:slugOrId/shop/:productSlug" element={<ProducerProductDetail />} />
        <Route path="/producer/:slugOrId/shop/:productSlug/bestall" element={<ProducerProductOrderPage />} />
        <Route path="/loppis/:fleaMarketId" element={<FleaMarketDetailPage />} />
        <Route path="/loppis/:fleaMarketId/tillfalle/:occurrenceId" element={<FleaMarketOccurrenceDetailPage />} />
        <Route path="/event/:eventId" element={<EventDetailPage />} />
        <Route path="/embed/producer/:slug" element={<ProducerActivitiesEmbedPage />} />
        <Route path="/embed/activity/:id" element={<ActivityEmbedPage />} />
        <Route path="/embed/product/:id" element={<ProductEmbedPage />} />
        <Route path="/inspiration/:slug" element={<CuratedWeekendPage />} />
        <Route path="/om-oss" element={<About />} />
        <Route path="/about" element={<Navigate to="/om-oss" replace />} />
        <Route path="/kontakt" element={<KontaktPage />} />
        <Route path="/villkor" element={<VillkorPage />} />
        <Route path="/integritet" element={<IntegritetPage />} />
        <Route path="/privacy" element={<IntegritetPage />} />
        <Route path="/sparat" element={<FavoritesPage />} />
        
        <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
        
        {/* Public legal pages */}
        <Route path="/legal/producer-terms" element={<ProducerTermsLegalPage />} />
        
        {/* Public booking routes */}
        <Route path="/booking/cancel" element={<BookingCancelPage />} />
        <Route path="/booking/cancel-accommodation" element={<AccommodationBookingCancelPage />} />
        <Route path="/accommodation/:accommodationId" element={<AccommodationDetailPage />} />
        
        {/* Public auth routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/auth/reset-password" element={<ResetPassword />} />
        <Route path="/accept-invite" element={<AcceptInvitePage />} />
        <Route path="/401" element={<Unauthorized />} />
        <Route path="/500" element={<Error500 />} />
        
        {/* Producer interest funnel – same wizard for signed-out and logged-in users */}
        <Route path="/for-producers" element={<AuthedForProducersRedirect />}>
          <Route index element={<ForProducersEntry />} />
          <Route path="qualify" element={<ForProducersQualify />} />
          <Route path="qualify/success" element={<ForProducersQualifySuccess />} />
          <Route path="apply" element={<ForProducersApply />} />
        </Route>
        
        {/* Legacy: redirect old onboarding to new interest page */}
        <Route path="/producer/onboarding" element={<AuthedForProducersRedirect />} />

        {/* Admin login/callback are public but separate */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/callback" element={<AdminCallback />} />

        {/* ============================================ */}
        {/* UNIFIED APP SHELL - All logged-in routes    */}
        {/* ============================================ */}
        <Route element={<AuthRouteGuard />}>
          {/* Suggest page – own layout, no app shell chrome */}
          <Route path="/suggest" element={<SuggestPage />} />
          
          {/* Setup page - own clean layout, no portal chrome */}
          <Route element={<HostRouteGuard />}>
            <Route element={<ProducerSetupLayout />}>
              <Route path="/app/producer/farm/:farmId/setup" element={<ProducerSetupPage />} />
            </Route>
          </Route>
          <Route path="/app" element={<UnifiedAppLayout />}>
            
            {/* Customer portal routes */}
            <Route path="customer" element={<CustomerOverview />} />
            <Route path="customer/bookings" element={<MyBookingsPage />} />
            <Route path="customer/bookings/:bookingId" element={<CustomerBookingDetailPage />} />
            <Route path="customer/orders" element={<MyOrdersPage />} />
            <Route path="customer/orders/:orderId" element={<OrderDetailPage />} />
            <Route path="customer/profile" element={<CustomerProfilePage />} />
            <Route path="customer/invitations" element={<InvitationsPage />} />
            
            {/* Producer onboarding – inside app shell, no host guard needed */}
            <Route path="producer/onboarding" element={<AppProducerOnboarding />} />
            <Route path="producer/onboarding/success" element={<OnboardingSuccessPage />} />
            
            {/* Producer entry – state-driven, no host guard needed */}
            <Route path="producer" element={<ProducerPortal />} />
            
            {/* Producer portal routes – guarded by HostRouteGuard */}
            <Route element={<HostRouteGuard />}>
              {/* Terms page - accessible without terms gate */}
              <Route path="producer/farm/:farmId/terms" element={<ProducerTermsPage />} />
              {/* All other producer routes wrapped in terms gate */}
              <Route path="producer/farm/:farmId" element={<ProducerPortalGate />}>
                <Route index element={<ProducerFarmDashboard />} />
                <Route path="insights" element={<ProducerDashboardPage />} />
                <Route path="welcome" element={<ProducerWelcomeFlowPage />} />
                <Route path="verifiering" element={<ProducerFeatureGate featureKey="producer_portal.show_verification"><ProducerVerificationPage /></ProducerFeatureGate>} />
                <Route path="grundinfo" element={<ProducerGrundinfo />} />
                <Route path="oversattningar" element={<ProducerOversattningar />} />
                <Route path="plats" element={<ProducerPlacePresentationPage />} />
                <Route path="mer" element={<ProducerMerPage />} />
                <Route path="kontakt" element={<ProducerKontakt />} />
                <Route path="kategorier" element={<ProducerKategorier />} />
                <Route path="bilder" element={<ProducerBilder />} />
                <Route path="publicering" element={<ProducerPublicering />} />
                <Route path="shop" element={<ProducerFeatureGate featureKey="producer_portal.show_shop" effectiveFeatureKey="farm_shop"><ProducerShopOverview /></ProducerFeatureGate>} />
                <Route path="shop/products" element={<ProducerFeatureGate featureKey="producer_portal.show_shop" effectiveFeatureKey="farm_shop"><ProducerProductList /></ProducerFeatureGate>} />
                <Route path="shop/products/new" element={<ProducerFeatureGate featureKey="producer_portal.show_shop" effectiveFeatureKey="farm_shop"><ProducerProductCreate /></ProducerFeatureGate>} />
                <Route path="shop/products/:productId/edit" element={<ProducerFeatureGate featureKey="producer_portal.show_shop" effectiveFeatureKey="farm_shop"><ProducerProductEdit /></ProducerFeatureGate>} />
                <Route path="shop/orders" element={<ProducerFeatureGate featureKey="producer_portal.show_shop" effectiveFeatureKey="farm_shop"><ProducerOrdersPage /></ProducerFeatureGate>} />
                <Route path="shop/pickup-windows" element={<ProducerFeatureGate featureKey="producer_portal.show_shop" effectiveFeatureKey="farm_shop"><ProducerPickupWindowsPage /></ProducerFeatureGate>} />
                <Route path="shop/pack-list" element={<ProducerFeatureGate featureKey="producer_portal.show_shop" effectiveFeatureKey="farm_shop"><ProducerPackListPage /></ProducerFeatureGate>} />
                <Route path="shop/shipping" element={<ProducerFeatureGate featureKey="producer_portal.show_shop" effectiveFeatureKey="farm_shop"><ProducerShippingPage /></ProducerFeatureGate>} />
                <Route path="events" element={<ProducerEventsPage />} />
                <Route path="loppis" element={<ProducerFleaMarketsPage />} />
                <Route path="accommodation" element={<ProducerAccommodationsPage />} />
                <Route path="accommodation/new" element={<AccommodationEditorPage />} />
                <Route path="accommodation/:accommodationId/edit" element={<AccommodationEditorPage />} />
                <Route path="events/new" element={<EventEditorPage />} />
                <Route path="events/:eventId/edit" element={<EventEditorPage />} />
                <Route path="events/:eventId/bookings" element={<EventBookingsPage />} />
                <Route path="bookings" element={<ProducerBookingsHubPage />} />
                <Route path="bookings/accommodation/:bookingId" element={<AccommodationBookingDetailPage />} />
                <Route path="outings" element={<Navigate to="../events?type=outing" replace />} />
                <Route path="outings/new" element={<Navigate to="../events/new?type=outing" replace />} />
                <Route path="outings/:outingId" element={<OutingRedirect />} />
                <Route path="team" element={<ProducerFeatureGate featureKey="producer_portal.show_team"><ProducerTeamPage /></ProducerFeatureGate>} />
                <Route path="team/invitations" element={<ProducerFeatureGate featureKey="producer_portal.show_team"><ProducerTeamInvitationsPage /></ProducerFeatureGate>} />
                <Route path="team/member/:memberId" element={<ProducerFeatureGate featureKey="producer_portal.show_team"><ProducerTeamMemberPage /></ProducerFeatureGate>} />
                <Route path="billing" element={<ProducerBillingPage />} />
              </Route>
            </Route>
            
            {/* Editor (Ambassador) routes – role-gated by RLS, no admin needed */}
            <Route path="editor" element={<Suspense fallback={null}><EditorDashboardPage /></Suspense>} />
            <Route path="editor/onboarding" element={<Suspense fallback={null}><EditorOnboardingPage /></Suspense>} />
            <Route path="editor/places" element={<Suspense fallback={null}><EditorPlacesListPage /></Suspense>} />
            <Route path="editor/places/:placeId/edit" element={<Suspense fallback={null}><EditorPlaceEditPage /></Suspense>} />
            <Route path="editor/areas" element={<Suspense fallback={null}><EditorAreasListPage /></Suspense>} />
            <Route path="editor/areas/:areaId/edit" element={<Suspense fallback={null}><EditorAreaEditPage /></Suspense>} />
            <Route path="editor/publicering" element={<Suspense fallback={null}><SocialPublishingPage /></Suspense>} />
            <Route path="editor/inspiration" element={<Suspense fallback={null}><EditorCuratedPagesPage /></Suspense>} />

            {/* Admin portal routes - additional guard for admin role */}
            <Route element={<AdminRouteGuard />}>
              {/* Legal documents admin - outside AdminProducerProvider */}
              <Route path="admin/legal/producer-terms" element={<AdminLegalDocumentsPage />} />
              
              <Route
                element={
                  <AdminProducerProvider>
                    <Outlet />
                  </AdminProducerProvider>
                }
              >
                {/* Dashboard and system logs */}
                <Route path="admin/control-panel" element={<AdminControlPanelPage />} />
                <Route path="admin/dashboard" element={<AdminDashboardPage />} />
                <Route path="admin/system-logs" element={<AdminSystemLogsPage />} />
                <Route path="admin/email-outbox" element={<AdminEmailOutboxPage />} />
                
                {/* Places management */}
                <Route path="admin/places" element={<AdminPlacesPage />} />
                <Route path="admin/places/ingest" element={<AdminPlacesIngestPage />} />
                <Route path="admin/places/reports" element={<AdminPlaceReportsPage />} />
                <Route path="admin/places/new" element={<AdminPlaceEditPage />} />
                <Route path="admin/places/:placeId/edit" element={<AdminPlaceEditPage />} />
                <Route path="admin/places/:placeId/studio" element={<Suspense fallback={null}><AdminPlaceStudioPage /></Suspense>} />
                <Route path="admin/places/:placeId/editors" element={<Suspense fallback={null}><AdminPlaceEditorsPage /></Suspense>} />
                <Route path="admin/place-suggestions" element={<AdminPlaceSuggestionsPage />} />
                <Route path="admin/producer-interest" element={<AdminProducerInterestPage />} />
                
                {/* Editorial: Areas, Experience curation + Ambassadors */}
                <Route path="admin/areas" element={<AdminAreasPage />} />
                <Route path="admin/areas/new" element={<AdminAreaEditPage />} />
                <Route path="admin/areas/:areaId/edit" element={<AdminAreaEditPage />} />
                <Route path="admin/experiences" element={<AdminExperienceCurationPage />} />
                <Route path="admin/curated-pages" element={<Navigate to="/app/editor/inspiration" replace />} />
                <Route path="admin/ambassadors" element={<Suspense fallback={null}><AdminAmbassadorsPage /></Suspense>} />
                <Route path="admin/ambassadors/:userId" element={<Suspense fallback={null}><AdminAmbassadorDetailPage /></Suspense>} />
                
                {/* Producer management */}
                <Route path="admin/producers" element={<AdminProducers />} />
                <Route path="admin/producers/success" element={<AdminProducerSuccessPage />} />
                <Route path="admin/producers/new" element={<AdminProducerNew />} />
                
                <Route path="admin/producers/:producerId" element={<AdminProducerOverview />} />
                <Route path="admin/producers/:producerId/grundinfo" element={<AdminProducerGrundinfo />} />
                <Route path="admin/producers/:producerId/plats" element={<AdminProducerPlats />} />
                <Route path="admin/producers/:producerId/kontakt" element={<AdminProducerKontakt />} />
                <Route path="admin/producers/:producerId/kategorier" element={<AdminProducerKategorier />} />
                <Route path="admin/producers/:producerId/bilder" element={<AdminProducerBilder />} />
                <Route path="admin/producers/:producerId/publicering" element={<AdminProducerPublicering />} />
                <Route path="admin/producers/:producerId/products" element={<AdminProductList />} />
                <Route path="admin/producers/:producerId/products/new" element={<AdminProductCreate />} />
                <Route path="admin/producers/:producerId/products/:productId/edit" element={<AdminProductEdit />} />
                <Route path="admin/cleanup/drafts" element={<AdminDraftCleanup />} />
                <Route path="admin/system/host-debug" element={<AdminHostDebugPage />} />
                <Route path="admin/system/host-health" element={<AdminHostHealthPage />} />
              </Route>
            </Route>
          </Route>
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
      
      {/* Global cart components */}
      {!isEmbedRoute && <GlobalCartSheet />}
      {!isEmbedRoute && <MobileCartBar />}
    </ActivePortalProvider>
    </AuthSheetProvider>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <FarmCartProvider>
          <FavoritesProvider>
            <InlineBannerProvider>
              <Toaster />
              <Sonner />
              <AppErrorBoundary>
                <BrowserRouter>
                  <LocaleProvider>
                    <AppRoutes />
                  </LocaleProvider>
                </BrowserRouter>
              </AppErrorBoundary>
            </InlineBannerProvider>
          </FavoritesProvider>
        </FarmCartProvider>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
